import React from "react";
import type { Dayjs } from "dayjs";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";

import CustomHelper from "../../../../../../components/Helper/CustomHelper";
import UnderlineButton from "../../../../../../components/Custom/UnderlineButton";
import { MobileTimePickerWithIcon } from "../../../../../../components/Custom/TimeInput";
import { getCssVariableHandler } from "../../../../../../utils/styles/getCssVariables";
import { getUniversalDateTimeFormat } from "../../../../../../utils/methods/universalDateTimeFormat";
import styles from "../../sessions.module.css";
import type { PositionFormik, SessionDates } from "./positionForm.types";
import { DATE_KEY_FORMAT, getSessionDates, toDateKey } from "./positionForm.utils";
import MealOptionNotice from "../../../../../../components/MealOptionNotice";

/**
 * Small building blocks reused by every section of the Add/Edit Position form.
 */

// The picker is an untyped (@ts-nocheck) legacy component, so every prop is optional.
const TimePicker = MobileTimePickerWithIcon as React.ComponentType<Record<string, unknown>>;

const fontColor = () => getCssVariableHandler({ variableName: "--font-rgb" });
const pinkColor = () => getCssVariableHandler({ variableName: "--primary-pink-rgb" });

export const FieldError = ({ show, message }: { show?: unknown; message?: unknown }) =>
  show && typeof message === "string" && message ? (
    <span className={styles.error}>{message}</span>
  ) : null;

/** "TBD?" / "Pick time?" switch shown at the top-right corner of a field label. */
export const TbdToggle = ({ isTbd, onToggle }: { isTbd: boolean; onToggle: () => void }) => (
  <UnderlineButton
    title={
      <CustomHelper
        isText
        message={isTbd ? "Time in and out have already decided!" : "Time in and out to be determined!"}
      >
        {isTbd ? "Pick time?" : "TBD?"}
      </CustomHelper>
    }
    showUnderline={false}
    onClick={onToggle}
    styles={{
      position: "absolute",
      top: "0",
      right: "0",
      minWidth: "0 !important",
      padding: "0 !important",
      fontSize: "1rem",
      fontWeight: "600",
    }}
  />
);

/** Field label with the TBD switch next to it ("Time In" or "No. of hours"). */
export const LabelWithTbdToggle = ({
  label,
  isTbd,
  onToggle,
}: {
  label: string;
  isTbd: boolean;
  onToggle: () => void;
}) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "5px",
      "& label": { marginBottom: "0 !important", padding: "0 !important" },
    }}
  >
    <InputLabel>{label}</InputLabel>
    <TbdToggle isTbd={isTbd} onToggle={onToggle} />
  </Box>
);

/** The dual-format time picker with the look used across this form. */
export const ShiftTimePicker = ({
  name,
  value,
  onChange,
  wide = false,
}: {
  name: string;
  value: Dayjs | null;
  onChange: (picked: Dayjs | null) => void;
  wide?: boolean;
}) => (
  <Box
    className={`${styles.inputIconBox} ${styles.iconEnd} ${wide ? styles.inputIconBoxV2 : ""}`}
  >
    <TimePicker
      name={name}
      slotProps={{ textField: { placeholder: "Select time" } }}
      minutesStep={30}
      timeSteps={{ minutes: 30 }}
      ampm
      sx={{
        "@media (max-width: 600px)": { width: "100%" },
        border: "1px solid white",
        borderRadius: "8px",
        "& .MuiInputBase-input": { paddingRight: "0 !important" },
        "& input": { color: "white", padding: "12px" },
        "& button": { color: "white" },
        "& ul": { backgroundColor: "black" },
      }}
      value={value}
      onChange={onChange}
    />
  </Box>
);

/** Outlined MUI Select with the white border used by every dropdown in this form. */
export const OutlinedSelect = ({ children, ...selectProps }: React.ComponentProps<typeof Select>) => (
  <Box sx={{ position: "relative", height: "50px", display: "flex", alignItems: "center" }}>
    <Select
      {...selectProps}
      sx={{
        border: `1px solid ${fontColor()}`,
        borderRadius: "10px",
        color: fontColor(),
        "&:focus": { outline: "none", border: "none" },
        "&.MuiInputBase-root": {
          width: "100%",
          height: "100%",
          "& .MuiSvgIcon-root": { color: fontColor() },
        },
      }}
    >
      {children}
    </Select>
  </Box>
);

const SELECT_DATE = "Select date";

/** Dropdown with every day of the session. Value is a `YYYY-MM-DD` key. */
export const SessionDateSelect = ({
  name,
  session,
  value,
  onChange,
  onBlur,
}: {
  name: string;
  session: SessionDates;
  value?: string;
  onChange: (dateKey: string) => void;
  onBlur?: React.FocusEventHandler;
}) => (
  <OutlinedSelect
    name={name}
    value={value && value !== SELECT_DATE ? toDateKey(value) : SELECT_DATE}
    onChange={(e) => onChange(String(e?.target?.value))}
    onBlur={onBlur}
  >
    <MenuItem className={styles.option} value={SELECT_DATE} disabled>
      {SELECT_DATE}
    </MenuItem>
    {getSessionDates(session).map((day) => (
      <MenuItem
        key={day.format(DATE_KEY_FORMAT)}
        value={day.format(DATE_KEY_FORMAT)}
        className={styles.option}
      >
        {getUniversalDateTimeFormat({ dateTime: day })}
      </MenuItem>
    ))}
  </OutlinedSelect>
);

/** "Overnight" label + checkbox. */
export const OvernightCheckbox = ({
  name,
  checked,
  onChange,
  onBlur,
  hideLabel = false,
  error,
}: {
  name: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  onBlur?: React.FocusEventHandler;
  hideLabel?: boolean;
  error?: React.ReactNode;
}) => (
  <Box className={styles["form-box"]}>
    <Tooltip title="Overnight shift">
      <InputLabel sx={{ textAlign: "center", visibility: hideLabel ? "hidden" : "visible" }}>
        Overnight
      </InputLabel>
    </Tooltip>
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50px" }}>
      <Checkbox
        name={name}
        checked={!!checked}
        onChange={(e) => onChange(e?.target?.checked)}
        onBlur={onBlur}
        sx={{ color: pinkColor(), padding: "0", height: "100%", alignSelf: "center" }}
      />
    </Box>
    {error}
  </Box>
);

/** "Add positions for each date of the session" switch (list and calendar views). */
export const EachDateOfSessionCheckbox = ({
  values,
  setValues,
}: Pick<PositionFormik, "values" | "setValues">) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
    <Checkbox
      checked={!!values?.eachDateOfSession}
      onChange={(e) =>
        setValues((prev) => ({
          ...prev,
          eachDateOfSession: e?.target?.checked,
          eachDateOfSessionChangedFromInput: true,
        }))
      }
      sx={{ color: fontColor(), padding: "0" }}
    />
    <Typography>Add positions for each date of the session.</Typography>
  </Box>
);

const MEAL_OPTIONS = [
  { value: "meal provided", label: "Meal Provided" },
  { value: "1 hour walk away", label: "1 hour walk away" },
];

export const MealOptionsField = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
}: PositionFormik) => (
  <Box className={styles["form-box"]}>
    <InputLabel>Meal options:</InputLabel>
    <FormControl>
      <RadioGroup name="option" onChange={handleChange} onBlur={handleBlur} value={values?.option}>
        {MEAL_OPTIONS.map((meal) => (
          <FormControlLabel
            key={meal.value}
            value={meal.value}
            label={meal.label}
            control={
              <Radio
                checked={values?.option === meal.value}
                sx={{ color: "#D175B6", "&.Mui-checked": { color: "#D175B6" }, padding: "0 9px" }}
              />
            }
          />
        ))}
      </RadioGroup>
    </FormControl>
    <MealOptionNotice rows={[{ selectOption: values?.option }]} compact />
    <FieldError show={touched?.option} message={errors?.option} />
  </Box>
);
